import { useState, useEffect, useCallback, useMemo } from "react";
import "@/App.css";
import { Check, Moon, Clock, Share2, Bell, BellOff, Heart, MessageCircle, Sparkles, Sun, BookOpen, Hand, Settings, Filter, Target, Star } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Toaster, toast } from "sonner";
import { 
  requestNotificationPermission, 
  registerServiceWorker, 
  onForegroundMessage,
  needsPWAInstall
} from "@/lib/firebase";
import { translations, ramadanDeeds, languages, dateLocales, bosnianMonths, bosnianWeekdays } from "@/i18n/translations";

// New 300 deeds data
import { deedsData, CONTEXT_TAGS, filterDeedsByContext, mapCategoryToKey } from "@/data/deeds";

// Constants & Utils
import { 
  CATEGORIES, 
  CATEGORY_LABELS, 
  CATEGORY_STATS_LABELS, 
  DEED_CATEGORIES,
  getDeedCategoryByIndex,
  getDeedCategoryById,
  getDeedCategory 
} from "@/constants/categories";
import { 
  RAMADAN_START, 
  RAMADAN_END, 
  getDeedIndexForDate, 
  getAppDay, 
  getCurrentDisplayDate, 
  isRamadanPeriod, 
  getRamadanDay,
  getRamadanDeedIndex,
  getIslamicDate,
  getLocalDateString,
  isEidDay
} from "@/utils/dateUtils";
import { getStreakEmoji, getBadges } from "@/utils/streakUtils";
import { CategoryIcon, CategoryBadge, AnimatedCounter, CategoryRing } from "@/components/progress";
import { 
  NotificationPrompt, 
  ReviewPrompt, 
  PostDeedModal, 
  CategoryStatsModal, 
  CategoryFocusModal, 
  FeedbackModal,
  ContextFilterModal 
} from "@/components/modals";
import { DeedCard } from "@/components/cards";
import { DeedCarousel } from "@/components/selection";
import { OnboardingFlow } from "@/components/onboarding";
import { useStreak } from "@/hooks";
import IftarCountdown from "@/components/ramadan/IftarCountdown";
import RamadanProgress from "@/components/ramadan/RamadanProgress";

function App() {
  const [showSplash, setShowSplash] = useState(false); // Splash screen disabled
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [completedDates, setCompletedDates] = useState([]);
  const [completedDeedIds, setCompletedDeedIds] = useState({}); // { "2026-01-23": [0, 2] } - welche Taten-Indizes wurden an welchem Tag abgeschlossen
  const [todayCompleted, setTodayCompleted] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [countdown, setCountdown] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [ramadanMode, setRamadanMode] = useState(false);
  const [manualRamadanMode, setManualRamadanMode] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [fcmToken, setFcmToken] = useState(null);
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  const [showReviewPrompt, setShowReviewPrompt] = useState(false);
  const [language, setLanguage] = useState('de');
  const [showPostDeedModal, setShowPostDeedModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null); // Für Kategorie-Statistik Modal
  const [isAfterIftar, setIsAfterIftar] = useState(false); // Nach Maghrib - warmer Modus
  const [showLailatulQadr, setShowLailatulQadr] = useState(false); // Laylatul-Qadr Animation
  
  // Swipe-Karussell State
  const [currentDeedIndex, setCurrentDeedIndex] = useState(0); // 0, 1, oder 2
  
  // Tat-Auswahl: null = noch nicht gewählt, Zahl = gewählter Index
  const [selectedDeedForToday, setSelectedDeedForToday] = useState(null);
  
  // Modal für Deed-Auswahl
  const [showDeedSelectionModal, setShowDeedSelectionModal] = useState(false);
  
  // Premium Feature: Kategorie-Fokus
  const [focusCategory, setFocusCategory] = useState(null); // null = alle Kategorien
  const [showCategorySetup, setShowCategorySetup] = useState(false);
  
  // Kontext-Filter State
  // activeContexts = aktuell aktive Filter (für heute)
  // pendingContexts = gewählte Filter (werden morgen aktiv)
  const [activeContexts, setActiveContexts] = useState([]); // Aktuell aktive Filter
  const [pendingContexts, setPendingContexts] = useState([]); // Für morgen geplante Filter
  const [showContextFilterModal, setShowContextFilterModal] = useState(false);
  
  // Legacy alias für Kompatibilität
  const selectedContexts = pendingContexts;
  
  // Feedback Modal
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  
  // User Name and Gender from onboarding
  const [userName, setUserName] = useState('');
  const [userGender, setUserGender] = useState('male');
  
  // Rotating motivational quotes - Initialize with random index
  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(() => Math.floor(Math.random() * 6));
  
  // Islamic motivational quotes - rotates under logo
  const motivationalQuotes = useMemo(() => ({
    de: [
      "Allah liebt das Beständige, auch wenn es klein ist.",
      "Nicht die Menge zählt, sondern die Aufrichtigkeit.",
      "Ein Schritt heute ist genug.",
      "Was mit reiner Absicht beginnt, wächst.",
      "Kleine Taten, große Belohnungen.",
      "Beständigkeit ist der Schlüssel.",
    ],
    en: [
      "Allah loves what is consistent, even if it is small.",
      "It's not quantity that counts, but sincerity.",
      "One step today is enough.",
      "What begins with pure intention grows.",
      "Small deeds, great rewards.",
      "Consistency is the key.",
    ],
    bs: [
      "Allah voli ono što je postojano, makar bilo malo.",
      "Ne broji se količina, već iskrenost.",
      "Jedan korak danas je dovoljan.",
      "Ono što počinje s čistom namjerom, raste.",
      "Mala djela, velike nagrade.",
      "Dosljednost je ključ.",
    ],
    ar: [
      "أحب الأعمال إلى الله أدومها وإن قل",
      "ليست الكمية المهمة، بل الإخلاص",
      "خطوة واحدة اليوم تكفي",
      "ما بُدئ بنية صادقة ينمو",
      "أعمال صغيرة، أجور عظيمة",
      "الاستمرارية هي المفتاح",
    ],
    id: [
      "Allah mencintai yang konsisten, meski kecil.",
      "Bukan jumlah yang penting, tapi keikhlasan.",
      "Satu langkah hari ini sudah cukup.",
      "Apa yang dimulai dengan niat tulus akan tumbuh.",
      "Amal kecil, pahala besar.",
      "Konsistensi adalah kuncinya.",
    ],
    tr: [
      "Allah tutarlı olanı sever, küçük olsa bile.",
      "Önemli olan miktar değil, samimiyet.",
      "Bugün bir adım yeterli.",
      "Saf niyetle başlayan büyür.",
      "Küçük ameller, büyük ödüller.",
      "Tutarlılık anahtardır.",
    ]
  }), []);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  
  // App Store URL
  const APP_STORE_URL = "https://apps.apple.com/app/id6757438087";

  // Get translations for current language
  const t = translations[language];
  
  // Use new 300 deeds data - filtered by ACTIVE context (not pending)
  const currentGoodDeeds = useMemo(() => {
    const allDeeds = deedsData[language] || deedsData.de;
    if (activeContexts.length === 0) {
      return allDeeds;
    }
    return filterDeedsByContext(allDeeds, activeContexts);
  }, [language, activeContexts]);
  
  const currentRamadanDeeds = ramadanDeeds[language];

  // Streak & Category Stats Hook
  const { currentStreak, categoryStats, totalCompleted } = useStreak(completedDates, completedDeedIds);

  // deedDay = für Tat-Auswahl (wechselt um 6:00 Uhr)
  // displayDate = für Anzeige (immer aktuelles Datum)
  const [deedDay, setDeedDay] = useState(getAppDay());
  const [displayDate, setDisplayDate] = useState(getCurrentDisplayDate());
  
  // Onboarding prüfen beim ersten Start
  useEffect(() => {
    const onboardingComplete = localStorage.getItem('goodDeeds_onboardingComplete');
    if (!onboardingComplete) {
      setShowOnboarding(true);
    }
    // Load user name
    const savedName = localStorage.getItem('goodDeeds_userName');
    if (savedName) {
      setUserName(savedName);
    }
    // Load user gender
    const savedGender = localStorage.getItem('goodDeeds_userGender');
    if (savedGender) {
      setUserGender(savedGender);
    }
    // Load saved context filters
    const savedContexts = localStorage.getItem('goodDeeds_selectedContexts');
    const savedActiveContexts = localStorage.getItem('goodDeeds_activeContexts');
    const savedContextDate = localStorage.getItem('goodDeeds_contextDate');
    const today = getLocalDateString();
    
    if (savedContexts) {
      try {
        const parsed = JSON.parse(savedContexts);
        if (Array.isArray(parsed)) {
          setPendingContexts(parsed);
          
          // Wenn es einen gespeicherten active Context gibt UND das Datum heute ist,
          // verwende diesen. Sonst übernehme die pending als active.
          if (savedActiveContexts && savedContextDate === today) {
            const activeParsed = JSON.parse(savedActiveContexts);
            if (Array.isArray(activeParsed)) {
              setActiveContexts(activeParsed);
            }
          } else {
            // Neuer Tag - pending wird zu active
            setActiveContexts(parsed);
            localStorage.setItem('goodDeeds_activeContexts', JSON.stringify(parsed));
            localStorage.setItem('goodDeeds_contextDate', today);
          }
        }
      } catch (e) {
        console.warn('Could not parse saved contexts');
      }
    }
    
    // Randomize starting quote for splash screen
    const quotes = motivationalQuotes[language] || motivationalQuotes.de;
    setCurrentQuoteIndex(Math.floor(Math.random() * quotes.length));
  }, [language, motivationalQuotes]);
  
  // Splash screen auto-hide after 2.5 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSplash(false);
    }, 2500);
    return () => clearTimeout(timer);
  }, []);

  // Onboarding abgeschlossen Handler
  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    // Notification Prompt nach kurzem Delay zeigen
    setTimeout(() => {
      const promptSeen = localStorage.getItem('goodDeeds_notificationPromptSeen');
      if (!promptSeen) {
        setShowNotificationPrompt(true);
      }
    }, 1000);
  };
  
  // Prüfe alle 60 Sekunden ob sich der Tag geändert hat
  useEffect(() => {
    const checkDayChange = () => {
      const newDeedDay = getAppDay();
      const newDisplayDate = getCurrentDisplayDate();
      
      if (newDeedDay !== deedDay) {
        setDeedDay(newDeedDay);
      }
      if (newDisplayDate !== displayDate) {
        setDisplayDate(newDisplayDate);
        // Reset bei neuem Tag - prüfe ob neues Datum bereits erledigt
        const saved = localStorage.getItem('goodDeeds_completed');
        if (saved) {
          const parsed = JSON.parse(saved);
          setTodayCompleted(parsed.includes(newDisplayDate));
        } else {
          setTodayCompleted(false);
        }
      }
    };
    
    const interval = setInterval(checkDayChange, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [deedDay, displayDate]);
  
  // Ramadan Modus - automatisch oder manuell
  const isRamadanActive = ramadanMode || manualRamadanMode;
  const ramadanDay = getRamadanDay();
  const islamicDate = isRamadanActive ? getIslamicDate(ramadanDay) : null;
  
  // Blessed Nights Badge - Last 10 nights of Ramadan (Laylatul Qadr)
  const blessedNightsRemaining = useMemo(() => {
    if (!isRamadanActive || ramadanDay === 0) return 0;
    if (ramadanDay >= 21) {
      return 30 - ramadanDay + 1; // Days remaining in last 10 nights
    }
    return 0;
  }, [isRamadanActive, ramadanDay]);
  
  // Eid Day Check
  const isEid = isEidDay();
  
  // Eid Greetings per language
  const eidGreetings = useMemo(() => ({
    de: `Eid Mubarak, ${userName || 'Geschwister'}!`,
    en: `Eid Mubarak, ${userName || 'dear one'}!`,
    bs: `Bajram Serif Mubarek Olsun, ${userName || 'dragi'}!`,
    ar: `عيد مبارك، ${userName || 'أخي/أختي'}!`,
    id: `Selamat Hari Raya, ${userName || 'saudara'}!`,
    tr: `Bayramınız kutlu olsun, ${userName || 'kardeş'}!`,
  }), [userName]);

  // Formatiertes Datum anzeigen
  const formatDate = useCallback((dateString) => {
    const date = new Date(dateString);
    
    // Manual formatting for Bosnian (browser locale not fully supported)
    if (language === 'bs') {
      const weekday = bosnianWeekdays.long[date.getDay()];
      const day = date.getDate();
      const month = bosnianMonths.long[date.getMonth()];
      const year = date.getFullYear();
      return `${weekday}, ${day}. ${month} ${year}`;
    }
    
    const options = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' };
    return date.toLocaleDateString(dateLocales[language], options);
  }, [language]);
  
  // Gefilterte Taten nach Kategorie (für Premium-Feature) - verwendet neue Datenstruktur
  const filteredDeeds = useMemo(() => {
    if (!focusCategory) return currentGoodDeeds;
    return currentGoodDeeds.filter((deed) => mapCategoryToKey(deed.category) === focusCategory);
  }, [currentGoodDeeds, focusCategory]);
  
  // 3 Taten für den Tag generieren - basierend auf Datum und neue 300 Taten
  const getDailyDeeds = useCallback(() => {
    if (isRamadanActive) {
      const deedIdx = getRamadanDeedIndex();
      const deed = currentRamadanDeeds[deedIdx];
      return [{ id: `ramadan-${deedIdx + 1}`, text: deed.text, source: deed.source, tafsir: deed.tafsir, isRamadan: true, day: ramadanDay > 0 ? ramadanDay : 1, originalIndex: deedIdx, category: 'ibadah' }];
    }
    
    // Verwende gefilterte Taten (nach Kategorie und Kontext bereits gefiltert)
    const availableDeeds = filteredDeeds;
    if (availableDeeds.length === 0) return [];
    
    // Basis-Index für den Tag
    const baseDeedIndex = getDeedIndexForDate(new Date(deedDay), availableDeeds.length);
    
    // Heute bereits abgeschlossene Tat-IDs
    const todayCompletedIds = completedDeedIds[displayDate] || [];
    
    // Berechne 3 gut verteilte Indizes über die verfügbaren Taten
    const spreadFactor = Math.floor(availableDeeds.length / 3);
    const spreadOffsets = [0, spreadFactor, spreadFactor * 2];
    
    const deeds = [];
    
    for (let offsetIndex = 0; offsetIndex < spreadOffsets.length; offsetIndex++) {
      const targetIndex = (baseDeedIndex + spreadOffsets[offsetIndex]) % availableDeeds.length;
      
      // Suche ab diesem Index nach einer passenden Tat
      let found = false;
      for (let searchOffset = 0; searchOffset < 30 && !found; searchOffset++) {
        const checkIndex = (targetIndex + searchOffset) % availableDeeds.length;
        const deed = availableDeeds[checkIndex];
        
        // Kategorie aus der neuen Datenstruktur
        const deedCategory = mapCategoryToKey(deed.category);
        const categoryMatches = !focusCategory || deedCategory === focusCategory;
        
        // Prüfe ob diese Tat heute schon erledigt wurde (nach ID)
        const alreadyCompleted = todayCompletedIds.includes(deed.id);
        
        // Prüfe ob diese Tat nicht schon in den heutigen 3 ist
        const alreadyInList = deeds.some(d => d.id === deed.id);
        
        if (categoryMatches && !alreadyCompleted && !alreadyInList) {
          deeds.push({
            id: deed.id,
            title: deed.title,
            text: deed.description, // Für Kompatibilität
            description: deed.description,
            source: `${deed.source} - ${deed.source_reference}`,
            source_reference: deed.source_reference,
            context_tags: deed.context_tags,
            isRamadan: false,
            originalIndex: checkIndex,
            category: deedCategory
          });
          found = true;
        }
      }
    }
    
    // Fallback: Falls weniger als 3 gefunden, fülle mit beliebigen Taten auf
    if (deeds.length < 3) {
      for (let i = 0; i < availableDeeds.length && deeds.length < 3; i++) {
        const checkIndex = (baseDeedIndex + i) % availableDeeds.length;
        const deed = availableDeeds[checkIndex];
        const deedCategory = mapCategoryToKey(deed.category);
        const categoryMatches = !focusCategory || deedCategory === focusCategory;
        
        if (categoryMatches && !deeds.some(d => d.id === deed.id)) {
          deeds.push({
            id: deed.id,
            title: deed.title,
            text: deed.description,
            description: deed.description,
            source: `${deed.source} - ${deed.source_reference}`,
            source_reference: deed.source_reference,
            context_tags: deed.context_tags,
            isRamadan: false,
            originalIndex: checkIndex,
            category: deedCategory,
            alreadyCompleted: (completedDeedIds[displayDate] || []).includes(deed.id)
          });
        }
      }
    }
    
    return deeds;
  }, [isRamadanActive, ramadanDay, deedDay, filteredDeeds, currentRamadanDeeds, focusCategory, completedDeedIds, displayDate]);

  const dailyDeeds = getDailyDeeds();
  const currentDeed = dailyDeeds[currentDeedIndex] || dailyDeeds[0];
  
  // Reset wenn sich der Tag ändert + Lade gespeicherte Auswahl
  useEffect(() => {
    setCurrentDeedIndex(0);
    // Prüfe ob heute bereits eine Tat ausgewählt wurde
    const savedSelection = localStorage.getItem('goodDeeds_selectedDeed');
    if (savedSelection) {
      const parsed = JSON.parse(savedSelection);
      if (parsed.date === displayDate) {
        setSelectedDeedForToday(parsed.deedIndex);
        // Wenn eine Auswahl existiert, Modal nicht öffnen
        setShowDeedSelectionModal(false);
      } else {
        // Neuer Tag - Reset
        setSelectedDeedForToday(null);
        localStorage.removeItem('goodDeeds_selectedDeed');
      }
    } else {
      // Keine gespeicherte Auswahl
      setSelectedDeedForToday(null);
    }
  }, [displayDate]);
  
  // Modal öffnen wenn keine Tat gewählt und mehrere verfügbar
  useEffect(() => {
    if (selectedDeedForToday === null && !todayCompleted && dailyDeeds.length > 1 && !showOnboarding) {
      setShowDeedSelectionModal(true);
    } else if (selectedDeedForToday !== null || todayCompleted) {
      setShowDeedSelectionModal(false);
    }
  }, [selectedDeedForToday, todayCompleted, dailyDeeds.length, showOnboarding]);

  // Die ausgewählte Tat für heute (gespeichertes Objekt)
  const [selectedDeedOriginalIndex, setSelectedDeedOriginalIndex] = useState(null);
  const [selectedDeedCategory, setSelectedDeedCategory] = useState(null);
  const [selectedDeedId, setSelectedDeedId] = useState(null); // Neue ID-basierte Speicherung

  // Dynamisch berechnete selectedDeedData basierend auf aktueller Sprache
  const selectedDeedData = useMemo(() => {
    if (selectedDeedId === null && selectedDeedOriginalIndex === null) return null;
    
    // Suche zuerst in der vollständigen Datenbank, nicht nur in gefilterten Taten
    const allDeeds = deedsData[language] || deedsData.de;
    let deed = null;
    
    if (selectedDeedId !== null) {
      deed = allDeeds.find(d => d.id === selectedDeedId);
    }
    // Fallback auf currentGoodDeeds wenn nicht gefunden
    if (!deed && selectedDeedId !== null) {
      deed = currentGoodDeeds.find(d => d.id === selectedDeedId);
    }
    if (!deed && selectedDeedOriginalIndex !== null) {
      deed = currentGoodDeeds[selectedDeedOriginalIndex];
    }
    
    if (!deed) return null;
    
    return {
      id: deed.id,
      title: deed.title,
      text: deed.description || deed.text,
      description: deed.description,
      source: `${deed.source} - ${deed.source_reference}`,
      source_reference: deed.source_reference,
      context_tags: deed.context_tags,
      originalIndex: selectedDeedOriginalIndex,
      category: selectedDeedCategory || mapCategoryToKey(deed.category)
    };
  }, [selectedDeedId, selectedDeedOriginalIndex, selectedDeedCategory, currentGoodDeeds, language]);

  // Lade gespeicherte Tat-Daten beim Start
  useEffect(() => {
    const savedSelection = localStorage.getItem('goodDeeds_selectedDeed');
    if (savedSelection) {
      const parsed = JSON.parse(savedSelection);
      if (parsed.date === displayDate && (parsed.deedId !== undefined || parsed.originalIndex !== undefined)) {
        setSelectedDeedId(parsed.deedId || null);
        setSelectedDeedOriginalIndex(parsed.originalIndex);
        setSelectedDeedCategory(parsed.deedData?.category || null);
      }
    }
  }, [displayDate]);

  // Funktion zum Auswählen einer Tat
  const handleSelectDeed = useCallback(() => {
    const selectedDeed = dailyDeeds[currentDeedIndex];
    if (!selectedDeed) return;
    
    // Speichere ID, Index und Kategorie
    setSelectedDeedForToday(currentDeedIndex);
    setSelectedDeedId(selectedDeed.id);
    setSelectedDeedOriginalIndex(selectedDeed.originalIndex);
    setSelectedDeedCategory(selectedDeed.category);
    
    // Modal schließen
    setShowDeedSelectionModal(false);
    
    localStorage.setItem('goodDeeds_selectedDeed', JSON.stringify({
      date: displayDate,
      deedIndex: currentDeedIndex,
      deedId: selectedDeed.id,
      originalIndex: selectedDeed.originalIndex,
      deedData: { category: selectedDeed.category }
    }));
  }, [currentDeedIndex, dailyDeeds, displayDate]);

  // Countdown berechnen - bis Mitternacht (00:00)
  useEffect(() => {
    const calculateCountdown = () => {
      const now = new Date();
      let nextReset = new Date(now);
      
      // Nächster Reset ist immer um Mitternacht des nächsten Tages
      nextReset.setDate(nextReset.getDate() + 1);
      nextReset.setHours(0, 0, 0, 0);
      
      const diff = nextReset - now;
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      setCountdown({ hours, minutes, seconds });
    };

    calculateCountdown();
    const interval = setInterval(calculateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  // Load saved language
  useEffect(() => {
    const savedLanguage = localStorage.getItem('goodDeeds_language');
    if (savedLanguage && ['de', 'en', 'bs', 'ar', 'id', 'tr'].includes(savedLanguage)) {
      setLanguage(savedLanguage);
    }
  }, []);

  // Ramadan automatisch aktivieren
  useEffect(() => {
    setRamadanMode(isRamadanPeriod());
  }, []);

  // Firebase Push Notifications Setup
  useEffect(() => {
    const setupNotifications = async () => {
      const savedToken = localStorage.getItem('goodDeeds_fcmToken');
      const hasSeenPrompt = localStorage.getItem('goodDeeds_notificationPromptSeen');
      
      // Check actual permission status (for iOS native app)
      if ('Notification' in window) {
        const permission = Notification.permission;
        if (permission === 'granted' && savedToken) {
          setFcmToken(savedToken);
          setNotificationsEnabled(true);
        } else if (permission === 'denied') {
          // Permission was denied, clear local state
          localStorage.removeItem('goodDeeds_fcmToken');
          setNotificationsEnabled(false);
        } else if (savedToken) {
          // Have token but permission state unclear - trust localStorage
          setFcmToken(savedToken);
          setNotificationsEnabled(true);
        }
      } else if (savedToken) {
        // Notification API not available (iOS WebView), trust localStorage
        setFcmToken(savedToken);
        setNotificationsEnabled(true);
      }
      
      if (!savedToken && !hasSeenPrompt) {
        setTimeout(() => setShowNotificationPrompt(true), 2000);
      }

      await registerServiceWorker();

      try {
        onForegroundMessage((payload) => {
          toast(payload.notification?.title || 'Daily Deeds', {
            description: payload.notification?.body,
            duration: 5000,
          });
        });
      } catch (error) {
        console.log('Foreground messaging not available');
      }
    };

    setupNotifications();
    
    // Also listen for messages from native iOS app about permission changes
    const handleMessage = (event) => {
      if (event.data?.type === 'push-permission-state') {
        const state = event.data.state;
        if (state === 'granted') {
          const token = localStorage.getItem('goodDeeds_fcmToken');
          if (token) {
            setNotificationsEnabled(true);
            setFcmToken(token);
          }
        } else if (state === 'denied') {
          setNotificationsEnabled(false);
        }
      }
      if (event.data?.type === 'fcm-token' && event.data.token) {
        localStorage.setItem('goodDeeds_fcmToken', event.data.token);
        setFcmToken(event.data.token);
        setNotificationsEnabled(true);
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Handle notification prompt
  const handleNotificationPromptAccept = async () => {
    setShowNotificationPrompt(false);
    localStorage.setItem('goodDeeds_notificationPromptSeen', 'true');
    await toggleNotifications();
  };

  const handleNotificationPromptDismiss = () => {
    setShowNotificationPrompt(false);
    localStorage.setItem('goodDeeds_notificationPromptSeen', 'true');
  };

  // Toggle Push Notifications
  const toggleNotifications = async () => {
    if (notificationsEnabled) {
      if (fcmToken) {
        try {
          await fetch(`${BACKEND_URL}/api/notifications/unregister/${fcmToken}`, {
            method: 'DELETE',
          });
        } catch (error) {
          console.error('Error unregistering token:', error);
        }
      }
      localStorage.removeItem('goodDeeds_fcmToken');
      setFcmToken(null);
      setNotificationsEnabled(false);
      toast(t.notificationsDisabled, {
        description: t.notificationsDisabledDesc,
        duration: 3000,
      });
    } else {
      const token = await requestNotificationPermission();
      
      console.log('[Push] Token received:', token ? `${token.substring(0, 30)}... (length: ${token.length})` : 'null');
      
      // Handle iOS Safari case - needs PWA installation
      if (token === 'ios-safari-not-supported') {
        toast.info(t.iosSafariNotSupported || 'Push in Safari nicht verfügbar', {
          description: t.iosSafariInstallPWA || 'Bitte installiere die App: Teilen-Button → "Zum Home-Bildschirm". Oder nutze die TestFlight-App.',
          duration: 10000,
        });
        return;
      }
      
      // Handle native iOS app case - token can be actual FCM token or 'ios-native-app' fallback
      if (token) {
        const isRealFCMToken = typeof token === 'string' && token.length > 50 && token !== 'ios-native-app';
        const platform = navigator.userAgent.includes('PWAShell') ? 'ios' : 'web';
        
        console.log('[Push] Platform:', platform, 'Is real FCM token:', isRealFCMToken);
        
        try {
          // Register with backend if we have a real FCM token
          if (isRealFCMToken) {
            console.log('[Push] Registering token with backend...');
            const response = await fetch(`${BACKEND_URL}/api/notifications/register`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ token, platform, language }),
            });
            console.log('[Push] Backend registration response:', response.status);
          } else {
            console.log('[Push] Skipping backend registration - not a real FCM token');
          }
          
          localStorage.setItem('goodDeeds_fcmToken', token);
          setFcmToken(token);
          setNotificationsEnabled(true);
          
          // For web, also store in service worker
          if (platform === 'web' && 'serviceWorker' in navigator && navigator.serviceWorker.controller) {
            navigator.serviceWorker.controller.postMessage({
              type: 'STORE_TOKEN',
              token: token
            });
          }
          
          toast.success(t.notificationsEnabled, {
            description: platform === 'ios' 
              ? (t.iosNotificationsEnabled || 'Push-Benachrichtigungen sind jetzt aktiv!')
              : t.notificationsEnabledDesc,
            duration: 4000,
          });

          // Send test notification for web
          if (platform === 'web' && typeof token === 'string' && token.length > 50) {
            try {
              await fetch(`${BACKEND_URL}/api/notifications/send-test?token=${token}&language=${language}`, {
                method: 'POST',
              });
            } catch (e) {
              console.log('Test notification skipped');
            }
          }
        } catch (error) {
          console.error('Error registering token:', error);
          // Still mark as enabled for native iOS even if backend fails
          if (platform === 'ios') {
            setNotificationsEnabled(true);
            localStorage.setItem('goodDeeds_fcmToken', token);
            toast.success(t.notificationsEnabled, {
              description: t.iosNotificationsEnabled || 'Push-Benachrichtigungen sind jetzt aktiv!',
              duration: 4000,
            });
          } else {
            toast.error(t.enableError, {
              description: t.enableErrorDesc,
              duration: 3000,
            });
          }
        }
      } else {
        // Permission was denied
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        
        if (isIOS) {
          toast.error(t.iosPermissionDenied || 'Berechtigung verweigert', {
            description: t.iosPermissionDeniedDesc || 'Bitte aktiviere Mitteilungen in: Einstellungen → Daily 1 → Mitteilungen',
            duration: 6000,
          });
        } else {
          toast.error(t.permissionDenied, {
            description: t.permissionDeniedDesc,
            duration: 4000,
          });
        }
      }
    }
  };

  // Update notification language when user changes language
  const updateNotificationLanguage = useCallback(async (newLanguage) => {
    const token = localStorage.getItem('goodDeeds_fcmToken');
    if (token) {
      try {
        await fetch(`${BACKEND_URL}/api/notifications/language?token=${token}&language=${newLanguage}`, {
          method: 'PUT',
        });
      } catch (error) {
        console.log('Language update skipped');
      }
    }
  }, [BACKEND_URL]);

  // Laden der gespeicherten Daten
  useEffect(() => {
    const saved = localStorage.getItem('goodDeeds_completed');
    const savedDeedIds = localStorage.getItem('goodDeeds_completedDeedIds');
    const savedManualRamadan = localStorage.getItem('goodDeeds_manualRamadan');
    
    if (saved) {
      const parsed = JSON.parse(saved);
      setCompletedDates(parsed);
      // Prüfe ob HEUTE bereits erledigt wurde
      if (parsed.includes(displayDate)) {
        setTodayCompleted(true);
      }
    }
    
    if (savedDeedIds) {
      const parsedDeedIds = JSON.parse(savedDeedIds);
      setCompletedDeedIds(parsedDeedIds);
    }
    
    if (savedManualRamadan) {
      setManualRamadanMode(JSON.parse(savedManualRamadan));
    }

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'DEED_COMPLETED') {
          const completedDate = event.data.date;
          setCompletedDates(prev => {
            if (!prev.includes(completedDate)) {
              const newCompleted = [...prev, completedDate];
              localStorage.setItem('goodDeeds_completed', JSON.stringify(newCompleted));
              return newCompleted;
            }
            return prev;
          });
          // Prüfe gegen displayDate
          if (completedDate === displayDate) {
            setTodayCompleted(true);
          }
          toast.success(t.completed, {
            description: t.deedSavedNotification,
            duration: 3000,
          });
        }
      });
    }
  }, [displayDate, t]);

  // Language change handler
  const handleLanguageChange = (langCode) => {
    setLanguage(langCode);
    localStorage.setItem('goodDeeds_language', langCode);
    updateNotificationLanguage(langCode);
  };

  // Check if should show review prompt (after 5-7 completed deeds)
  const checkReviewPrompt = useCallback((completedCount) => {
    const hasReviewed = localStorage.getItem('goodDeeds_hasReviewed');
    const reviewDismissed = localStorage.getItem('goodDeeds_reviewDismissed');
    
    if (hasReviewed || reviewDismissed) return;
    
    // Show prompt after 5, 6, or 7 completed deeds
    if (completedCount >= 5 && completedCount <= 7) {
      const shownAt = localStorage.getItem('goodDeeds_reviewShownAt');
      if (!shownAt || parseInt(shownAt) !== completedCount) {
        localStorage.setItem('goodDeeds_reviewShownAt', completedCount.toString());
        setTimeout(() => setShowReviewPrompt(true), 1500);
      }
    }
  }, []);

  // Load focus category from localStorage
  useEffect(() => {
    const savedFocusCategory = localStorage.getItem('goodDeeds_focusCategory');
    if (savedFocusCategory) {
      setFocusCategory(savedFocusCategory === 'all' ? null : savedFocusCategory);
    }
  }, []);

  // Handle category focus selection
  const handleSelectFocusCategory = (category) => {
    setFocusCategory(category);
    localStorage.setItem('goodDeeds_focusCategory', category || 'all');
    setShowCategorySetup(false);
  };

  // Handle review button click
  const handleReviewClick = () => {
    localStorage.setItem('goodDeeds_hasReviewed', 'true');
    setShowReviewPrompt(false);
    window.open(APP_STORE_URL, '_blank');
  };

  // Handle review later
  const handleReviewLater = () => {
    setShowReviewPrompt(false);
  };

  // Handle never ask again
  const handleReviewNever = () => {
    localStorage.setItem('goodDeeds_reviewDismissed', 'true');
    setShowReviewPrompt(false);
  };

  // Tat abhaken (nach Auswahl)
  const handleComplete = useCallback(async () => {
    if (todayCompleted) return;
    
    // Hole die ausgewählte Tat - IMMER aus den gespeicherten IDs
    let deedIdToSave = selectedDeedId;
    let deedCategory = selectedDeedCategory;
    
    // Fallback 1: Prüfe localStorage direkt
    if (!deedIdToSave) {
      const savedSelection = localStorage.getItem('goodDeeds_selectedDeed');
      if (savedSelection) {
        const parsed = JSON.parse(savedSelection);
        if (parsed.date === displayDate && parsed.deedId) {
          deedIdToSave = parsed.deedId;
          deedCategory = parsed.deedData?.category;
        }
      }
    }
    
    // Fallback 2: Verwende die aktuell angezeigte Tat
    if (!deedIdToSave) {
      const currentDeedToSave = dailyDeeds[currentDeedIndex] || dailyDeeds[0];
      if (currentDeedToSave) {
        deedIdToSave = currentDeedToSave.id;
        deedCategory = currentDeedToSave.category;
      }
    }
    
    // Speichere das ANGEZEIGTE Datum
    const newCompleted = [...completedDates];
    if (!newCompleted.includes(displayDate)) {
      newCompleted.push(displayDate);
    }
    setCompletedDates(newCompleted);
    localStorage.setItem('goodDeeds_completed', JSON.stringify(newCompleted));
    
    // Speichere welche Tat erledigt wurde (IMMER die ID!)
    const newCompletedDeedIds = { ...completedDeedIds };
    if (!newCompletedDeedIds[displayDate]) {
      newCompletedDeedIds[displayDate] = [];
    }
    
    if (deedIdToSave) {
      newCompletedDeedIds[displayDate].push(deedIdToSave);
      console.log('Saved deed ID:', deedIdToSave, 'category:', deedCategory);
    }
    
    setCompletedDeedIds(newCompletedDeedIds);
    localStorage.setItem('goodDeeds_completedDeedIds', JSON.stringify(newCompletedDeedIds));
    
    setTodayCompleted(true);
    
    const token = localStorage.getItem('goodDeeds_fcmToken');
    if (token) {
      try {
        await fetch(`${BACKEND_URL}/api/deeds/complete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, date: displayDate })
        });
      } catch (e) {
        console.log('Backend sync skipped');
      }
    }
    
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 600);
    
    // Calculate streak for celebration (inline to avoid dependency issues)
    let streakCount = 0;
    const today = new Date();
    for (let i = 0; i < 365; i++) {
      const checkDate = new Date(today);
      checkDate.setDate(checkDate.getDate() - i);
      const dateStr = getLocalDateString(checkDate);
      if (newCompleted.includes(dateStr)) {
        streakCount++;
      } else if (i > 0) {
        break;
      }
    }
    
    // Confetti celebration for milestones!
    const milestoneMessages = {
      de: { 7: '🌱 7 Tage Beständigkeit!', 30: '⭐ 30 Tage Beständigkeit!', 100: '🏆 100 Tage Beständigkeit!' },
      en: { 7: '🌱 7 Days of Consistency!', 30: '⭐ 30 Days of Consistency!', 100: '🏆 100 Days of Consistency!' },
      bs: { 7: '🌱 7 Dana Dosljednosti!', 30: '⭐ 30 Dana Dosljednosti!', 100: '🏆 100 Dana Dosljednosti!' },
      ar: { 7: '🌱 7 أيام من الثبات!', 30: '⭐ 30 يوم من الثبات!', 100: '🏆 100 يوم من الثبات!' },
      id: { 7: '🌱 7 Hari Konsistensi!', 30: '⭐ 30 Hari Konsistensi!', 100: '🏆 100 Hari Konsistensi!' },
      tr: { 7: '🌱 7 Gün Tutarlılık!', 30: '⭐ 30 Gün Tutarlılık!', 100: '🏆 100 Gün Tutarlılık!' }
    };
    
    // Show post-deed Dua modal
    setShowPostDeedModal(true);

    // Laylatul-Qadr Special: ungerade Nächte der letzten 10 Tage (21, 23, 25, 27, 29)
    if (isRamadanActive && ramadanDay >= 21 && ramadanDay % 2 === 1) {
      setTimeout(() => setShowLailatulQadr(true), 800);
      setTimeout(() => setShowLailatulQadr(false), 6000);
    }

    // Check if we should show review prompt (after modal is closed)
    // checkReviewPrompt will be called when modal closes
  }, [completedDates, completedDeedIds, displayDate, selectedDeedId, selectedDeedCategory, dailyDeeds, currentDeedIndex, todayCompleted, BACKEND_URL, isRamadanActive, ramadanDay]);

  // Post-Deed Modal close handler
  const handlePostDeedClose = () => {
    setShowPostDeedModal(false);
    checkReviewPrompt(completedDates.length);
  };

  // Letzte 7 Tage für horizontale Wochenansicht
  const getWeekDays = useCallback(() => {
    const days = [];
    const now = new Date();
    const weekdayLabels = {
      de: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
      en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
      bs: ['Ned', 'Pon', 'Uto', 'Sri', 'Čet', 'Pet', 'Sub'],
      ar: ['أحد', 'إثن', 'ثلا', 'أرب', 'خمي', 'جمع', 'سبت'],
      id: ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'],
      tr: ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt']
    };
    
    // Letzte 7 Tage (von vor 6 Tagen bis heute)
    for (let i = 6; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      const dateString = getLocalDateString(date);
      const isCompleted = completedDates.includes(dateString);
      const isToday = dateString === displayDate;
      const dayOfWeek = date.getDay();
      
      // Kategorie der Tat für diesen Tag ermitteln
      const deedIdsForDay = completedDeedIds[dateString];
      let category = 'herz'; // Default
      
      if (deedIdsForDay && deedIdsForDay.length > 0) {
        // Verwende die letzte gewählte Tat für diesen Tag
        const deedId = deedIdsForDay[deedIdsForDay.length - 1];
        category = getDeedCategoryById(deedId);
      } else if (isCompleted) {
        // Fallback: Berechne Index aus Datum
        const deedIndex = getDeedIndexForDate(date, 100);
        category = getDeedCategoryByIndex(deedIndex);
      }
      
      days.push({
        date: dateString,
        dayLabel: weekdayLabels[language]?.[dayOfWeek] || weekdayLabels.de[dayOfWeek],
        dayNumber: date.getDate(),
        isCompleted,
        isToday,
        category
      });
    }
    
    return days;
  }, [completedDates, completedDeedIds, displayDate, language]);

  // currentDeedCategory - Kategorie der aktuellen Tat
  const currentDeedCategory = useMemo(() => {
    // Wenn eine Tat ausgewählt und gespeichert wurde, verwende deren Kategorie
    if (selectedDeedData) {
      if (selectedDeedData.category) return selectedDeedData.category;
      if (selectedDeedData.originalIndex !== undefined) return getDeedCategoryByIndex(selectedDeedData.originalIndex);
    }
    // Sonst: aktuelle Tat im Karussell
    if (currentDeed?.category) return currentDeed.category;
    if (currentDeed?.originalIndex !== undefined) return getDeedCategoryByIndex(currentDeed.originalIndex);
    const deedIndex = getDeedIndexForDate(new Date(deedDay), 100);
    return getDeedCategoryByIndex(deedIndex);
  }, [currentDeed, selectedDeedData, deedDay]);

  // Abgeschlossene Taten für eine bestimmte Kategorie holen
  const getCompletedDeedsForCategory = useCallback((category) => {
    const deeds = [];
    const allDeeds = deedsData[language] || deedsData.de;
    
    completedDates.forEach(dateString => {
      const date = new Date(dateString);
      
      // Prüfe, ob wir die tatsächlich gewählte Tat-ID haben
      const deedIdsForDay = completedDeedIds[dateString];
      let deedCategory = 'herz';
      let deed = null;
      
      if (deedIdsForDay && deedIdsForDay.length > 0) {
        // Verwende die letzte gewählte Tat für diesen Tag
        const deedId = deedIdsForDay[deedIdsForDay.length - 1];
        deedCategory = getDeedCategoryById(deedId);
        deed = allDeeds.find(d => d.id === deedId);
      } else {
        // Fallback: Berechne Index aus Datum (für alte Daten ohne gespeicherte IDs)
        const deedIndex = getDeedIndexForDate(date, 100);
        deedCategory = getDeedCategoryByIndex(deedIndex);
        deed = allDeeds[deedIndex % allDeeds.length];
      }
      
      if (deed && deedCategory === category) {
        // Datum formatieren
        let formattedDate;
        if (language === 'bs') {
          const weekday = bosnianWeekdays.short[date.getDay()];
          const day = date.getDate();
          const month = bosnianMonths.short[date.getMonth()];
          formattedDate = `${weekday}, ${day}. ${month}`;
        } else {
          formattedDate = date.toLocaleDateString(dateLocales[language], { 
            weekday: 'short', 
            day: 'numeric', 
            month: 'short' 
          });
        }
        deeds.push({
          date: dateString,
          formattedDate,
          text: deed.title || deed.text || deed.description
        });
      }
    });
    // Sortiere nach Datum (neueste zuerst)
    return deeds.sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [completedDates, completedDeedIds, language]);

  // Kategorie-Ring Klick Handler
  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
  };

  const closeCategoryModal = () => {
    setSelectedCategory(null);
  };

  // Teilen-Funktion (Web Share API / iOS Share Sheet)
  const handleShare = async () => {
    // Verwende die ausgewählte/angezeigte Tat
    const deedToShare = selectedDeedData || currentDeed;
    const deedText = deedToShare?.text || deedToShare?.description || '';
    const shareText = t.shareText.replace('{deed}', deedText);

    if (navigator.share) {
      try {
        await navigator.share({
          title: t.shareTitle,
          text: shareText,
        });
        toast.success(t.shareSuccess, {
          description: t.shareSuccessDesc,
          duration: 3000,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
        }
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareText);
        toast.success(t.textCopied, {
          description: t.textCopiedDesc,
          duration: 3000,
        });
      } catch (err) {
        console.error('Copy failed:', err);
      }
    }
  };

  // Motivierender Text für Stats
  const getStatsLabel = () => {
    if (totalCompleted === 0) return t.startToday;
    if (totalCompleted === 1) return t.firstDeed;
    return t.goodDeeds;
  };

  // Countdown formatieren
  const formatCountdown = () => {
    const pad = (n) => n.toString().padStart(2, '0');
    return `${pad(countdown.hours)}:${pad(countdown.minutes)}:${pad(countdown.seconds)}`;
  };

  // Get current language info
  const currentLang = languages.find(l => l.code === language) || languages[0];

  return (
    <TooltipProvider>
      <div className={`app-container ${isRamadanActive ? 'ramadan-active' : ''} ${isAfterIftar && isRamadanActive ? 'after-iftar' : ''}`} data-testid="app-container">
        {/* Laylatul-Qadr Spezial Animation */}
        {showLailatulQadr && (
          <div className="lailatul-qadr-overlay" data-testid="lailatul-qadr">
            <div className="lq-star lq-star-1" />
            <div className="lq-star lq-star-2" />
            <div className="lq-star lq-star-3" />
            <div className="lq-text-container">
              <p className="lq-text">{t.lailatulQadrText || 'Eine Nacht besser als tausend Monate.'}</p>
              <p className="lq-source">{t.lailatulQadrSource || '— Sure 97:3'}</p>
            </div>
          </div>
        )}
        {/* Individuelle funkelnde Sternen-Layer */}
        <div className="stars-layer-1" />
        <div className="stars-layer-2" />
        <div className="stars-layer-3" />
        
        <div className={`hero-glow ${isRamadanActive ? 'ramadan-glow' : ''}`} />
        <Toaster 
          position="top-center" 
          theme="dark"
          toastOptions={{
            style: {
              background: '#1E1E1E',
              border: '1px solid rgba(255,255,255,0.1)',
              color: '#EDEDED',
            },
          }}
        />
        
        {/* Splash Screen with Rotating Quote */}
        {showSplash && (
          <div className="splash-screen" data-testid="splash-screen">
            <div className="splash-content">
              <img src="/logo192.png" alt="Daily 1" className="splash-logo" />
              <p className="splash-quote">
                {(motivationalQuotes[language] || motivationalQuotes.de)[currentQuoteIndex]}
              </p>
            </div>
          </div>
        )}

        {/* Eid Mubarak Sticky Header */}
        {isEid && (
          <div className="eid-header" data-testid="eid-header">
            <span className="eid-icon">🌙✨</span>
            <span className="eid-text">{eidGreetings[language] || eidGreetings.de}</span>
          </div>
        )}

        <main className="main-content">
          {/* Modals */}
          <NotificationPrompt 
            show={showNotificationPrompt}
            t={t}
            onAccept={handleNotificationPromptAccept}
            onDismiss={handleNotificationPromptDismiss}
          />

          <ReviewPrompt 
            show={showReviewPrompt}
            t={t}
            onReview={handleReviewClick}
            onLater={handleReviewLater}
            onNever={handleReviewNever}
          />

          <PostDeedModal 
            show={showPostDeedModal}
            t={t}
            userGender={userGender}
            onComplete={handlePostDeedClose}
          />

          <CategoryStatsModal 
            category={selectedCategory}
            language={language}
            categoryStats={categoryStats}
            completedDeeds={selectedCategory ? getCompletedDeedsForCategory(selectedCategory) : []}
            t={t}
            onClose={closeCategoryModal}
          />

          <CategoryFocusModal 
            show={showCategorySetup}
            focusCategory={focusCategory}
            language={language}
            t={t}
            onSelectCategory={handleSelectFocusCategory}
            onClose={() => setShowCategorySetup(false)}
          />

          <FeedbackModal 
            show={showFeedbackModal}
            t={t}
            feedbackText={feedbackText}
            onFeedbackChange={setFeedbackText}
            onClose={() => setShowFeedbackModal(false)}
          />

          {/* Context Filter Modal */}
          <ContextFilterModal
            isOpen={showContextFilterModal}
            onClose={() => setShowContextFilterModal(false)}
            language={language}
            selectedContexts={pendingContexts}
            activeContexts={activeContexts}
            todayCompleted={todayCompleted}
            onApply={(contexts) => {
              setPendingContexts(contexts);
              // Speichere im localStorage
              localStorage.setItem('goodDeeds_selectedContexts', JSON.stringify(contexts));
              
              // Wenn heute noch keine Tat abgeschlossen wurde, aktiviere sofort
              if (!todayCompleted) {
                setActiveContexts(contexts);
                localStorage.setItem('goodDeeds_activeContexts', JSON.stringify(contexts));
                localStorage.setItem('goodDeeds_contextDate', getLocalDateString());
              }
              // Sonst: Änderung wird erst morgen aktiv (pending bleibt gespeichert)
            }}
          />

          {/* Onboarding Flow */}
          {showOnboarding && (
            <OnboardingFlow 
              onComplete={handleOnboardingComplete}
              language={language}
              onLanguageChange={(newLang) => setLanguage(newLang)}
            />
          )}

          {/* Header mit Logo und Sprachauswahl */}
          <div className="header-row">
            <div className={`logo-text ${isRamadanActive ? 'ramadan-logo' : ''}`} data-testid="app-logo">
              {isRamadanActive ? (
                <>
                  <Moon size={22} className="logo-icon" />
                  <span>{t.ramadanTitle}</span>
                </>
              ) : (
                <img src="/logo-blue.svg" alt="Daily 1" className="logo-img" />
              )}
            </div>
            
            {/* Blessed Nights Badge - Last 10 nights of Ramadan */}
            {isRamadanActive && blessedNightsRemaining > 0 && (
              <div className="blessed-nights-badge" data-testid="blessed-nights-badge">
                <Moon size={14} />
                <span>{t.blessedNights?.replace('{count}', blessedNightsRemaining) || `${blessedNightsRemaining} Nächte Segen`}</span>
              </div>
            )}
            
            {/* Header Actions */}
            <div className="header-actions" data-testid="header-actions">
              {/* Notification Toggle */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button 
                    className={`notification-btn ${notificationsEnabled ? 'active' : ''}`}
                    onClick={toggleNotifications}
                    data-testid="notification-toggle"
                  >
                    {notificationsEnabled ? <Bell size={16} /> : <BellOff size={16} />}
                  </button>
                </TooltipTrigger>
                <TooltipContent className="tooltip-content">
                  {notificationsEnabled ? t.disableNotifications : t.enableNotifications}
                </TooltipContent>
              </Tooltip>

              {/* Context Filter Button */}
              {/* Context Filter Button - Hidden during Ramadan */}
              {!isRamadanActive && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button 
                      className={`notification-btn ${activeContexts.length > 0 ? 'active' : ''}`}
                      onClick={() => setShowContextFilterModal(true)}
                      data-testid="context-filter-btn"
                    >
                      <Target size={16} />
                      {activeContexts.length > 0 && (
                        <span className="filter-count-badge">{activeContexts.length}</span>
                      )}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="tooltip-content">
                    {t.yourSituation || "Deine Situation"}
                  </TooltipContent>
                </Tooltip>
              )}

              {/* Category Focus Button */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button 
                    className={`notification-btn ${focusCategory ? 'active' : ''}`}
                    onClick={() => setShowCategorySetup(true)}
                    data-testid="category-focus-btn"
                  >
                    <Sparkles size={16} />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="tooltip-content">
                  {t.changeFocus || "Schwerpunkt ändern"}
                </TooltipContent>
              </Tooltip>

              {/* Language Selector */}
              <DropdownMenu>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuTrigger asChild>
                      <button 
                        className="language-btn"
                        data-testid="language-selector"
                      >
                        <span className="flag-icon">{currentLang.flag}</span>
                      </button>
                    </DropdownMenuTrigger>
                  </TooltipTrigger>
                  <TooltipContent className="tooltip-content">
                    {t.selectLanguage}
                  </TooltipContent>
                </Tooltip>
                <DropdownMenuContent align="end" className="language-dropdown">
                  {languages.map((lang) => (
                    <DropdownMenuItem 
                      key={lang.code}
                      onClick={() => handleLanguageChange(lang.code)}
                      className={`language-option ${language === lang.code ? 'active' : ''}`}
                      data-testid={`language-option-${lang.code}`}
                    >
                      <span className="flag-icon">{lang.flag}</span>
                      <span className="lang-name">{lang.name}</span>
                      {language === lang.code && <Check size={14} className="check-mark" />}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          
          {/* Personalized Greeting */}
          {userName && (
            <div className="personalized-greeting fade-in" data-testid="greeting">
              {t.greeting}, <span className="greeting-name">{userName}</span>
            </div>
          )}
          
          {/* Ramadan Badge */}
          {isRamadanActive && (
            <div className="ramadan-badge fade-in" data-testid="ramadan-badge">
              <Moon size={12} />
              <span>{t.ramadanDay.replace('{day}', currentDeed.day)}</span>
            </div>
          )}
          
          {/* Iftar Countdown - nur während Ramadan */}
          {isRamadanActive && (
            <IftarCountdown 
              language={language} 
              ramadanDay={ramadanDay}
              onAfterIftarChange={setIsAfterIftar}
            />
          )}
          
          {/* After-Iftar Nachricht */}
          {isRamadanActive && isAfterIftar && (
            <div className="after-iftar-card fade-in" data-testid="after-iftar-message">
              <div className="iftar-glow" />
              <p className="iftar-arabic">ذَهَبَ الظَّمَأُ وَابْتَلَّتِ الْعُرُوقُ وَثَبَتَ الْأَجْرُ إِنْ شَاءَ اللَّهُ</p>
              <p className="iftar-translit">Dhahaba aẓ-ẓama', wabtallati al-'urūq, wa thabata al-ajru in shā'Allāh</p>
              <div className="iftar-divider" />
              <p className="iftar-meaning">{t.afterIftarText || 'Möge dein Fasten angenommen sein.'}</p>
              <p className="iftar-source">— Abu Dawud</p>
            </div>
          )}
          
          {/* DeedCard - immer sichtbar */}
          <DeedCard
            displayDate={displayDate}
            formatDate={formatDate}
            currentDeedCategory={currentDeedCategory}
            selectedDeedData={selectedDeedData}
            currentDeed={currentDeed}
            todayCompleted={todayCompleted}
            showSuccess={showSuccess}
            isRamadanActive={isRamadanActive}
            islamicDate={islamicDate}
            t={t}
            onComplete={handleComplete}
            showChangeButton={!todayCompleted && dailyDeeds.length > 1}
            onChangeClick={() => {
              // Behalte den ausgewählten Index bei, wenn eine Tat gewählt wurde
              if (selectedDeedForToday !== null) {
                setCurrentDeedIndex(selectedDeedForToday);
              }
              setShowDeedSelectionModal(true);
            }}
          />
          
          {/* Deed Selection Modal Overlay - NUR wenn NICHT Ramadan */}
          {showDeedSelectionModal && !isRamadanActive && (
            <div className="deed-selection-overlay" data-testid="deed-selection-overlay">
              <DeedCarousel
                dailyDeeds={dailyDeeds}
                currentDeedIndex={currentDeedIndex}
                displayDate={displayDate}
                formatDate={formatDate}
                isRamadanActive={isRamadanActive}
                t={t}
                onDeedClick={(index) => setCurrentDeedIndex(index)}
                onSelectDeed={handleSelectDeed}
              />
            </div>
          )}
          
          {/* Unterer Bereich - immer anzeigen */}
          {/* Countdown Section - MIT TEXT */}
          <div className="countdown-section fade-in" style={{ animationDelay: '0.1s' }} data-testid="countdown-section">
                <div className="countdown-label">{t.nextDeedIn}</div>
                <div className="countdown-display">
                  <Clock size={20} className="clock-icon-animated" />
                  <span className="countdown-value" data-testid="countdown-display">
                    {formatCountdown()}
                  </span>
                </div>
              </div>
              
              {/* Stats Row */}
              <div className="stats-row fade-in" style={{ animationDelay: '0.15s' }} data-testid="stats-container">
                <div className="stat-card" data-testid="stat-total">
                  <div className="stat-value">{totalCompleted}</div>
                  <div className="stat-label">{getStatsLabel()}</div>
                </div>
                <div className="stat-card" data-testid="stat-streak">
                  <div className="stat-value">{currentStreak}</div>
                  <div className="stat-label">{t.currentStreak}</div>
                </div>
              </div>
              
              {/* Ramadan-Fortschritt ODER normaler Fortschritt */}
              {isRamadanActive ? (
                <div className="fade-in" style={{ animationDelay: '0.2s' }}>
                  <RamadanProgress
                    ramadanDay={ramadanDay}
                    completedDates={completedDates}
                    language={language}
                  />
                </div>
              ) : (
              <div className="progress-section fade-in" style={{ animationDelay: '0.2s' }} data-testid="progress-section">
                <div className="progress-header">
                  <span className="progress-title">{t.yourPath || 'Dein Weg'}</span>
                  {currentStreak >= 3 && (
                    <span className="streak-indicator">
                      {getStreakEmoji(currentStreak)} {currentStreak} {t.streakDays}
                    </span>
                  )}
                </div>
                
                {/* Kategorie-Ringe */}
                <div className="category-rings-container" data-testid="category-rings">
                  <CategoryRing 
                    category="ibadah" 
                    count={categoryStats.ibadah} 
                    total={totalCompleted} 
                    language={language}
                    onClick={() => handleCategoryClick('ibadah')}
                  />
                  <CategoryRing 
                    category="herz" 
                count={categoryStats.herz} 
                total={totalCompleted} 
                language={language}
                onClick={() => handleCategoryClick('herz')}
              />
              <CategoryRing 
                category="zunge" 
                count={categoryStats.zunge} 
                total={totalCompleted} 
                language={language}
                onClick={() => handleCategoryClick('zunge')}
              />
              <CategoryRing 
                category="handlung" 
                count={categoryStats.handlung} 
                total={totalCompleted} 
                language={language}
                onClick={() => handleCategoryClick('handlung')}
              />
            </div>
            
            {/* Horizontale Wochenansicht */}
            <div className="week-view" data-testid="week-view">
              <div className="week-days">
                {getWeekDays().map((day) => {
                  const categoryConfig = CATEGORIES[day.category];
                  const CategoryIconComponent = categoryConfig?.icon || Sparkles;
                  return (
                    <div 
                      key={day.date}
                      className={`week-day ${day.isToday ? 'today' : ''} ${day.isCompleted ? 'completed' : ''}`}
                      data-testid={`week-day-${day.date}`}
                    >
                      <span className="day-label">{day.dayLabel}</span>
                      <div className={`day-circle ${day.isCompleted ? 'filled' : 'empty'}`}>
                        {day.isCompleted ? (
                          <Check size={12} strokeWidth={2.5} />
                        ) : (
                          <span className="day-number">{day.dayNumber}</span>
                        )}
                      </div>
                      {day.isCompleted && (
                        <div className="day-category active" style={{ color: categoryConfig?.color }}>
                          <CategoryIconComponent size={10} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
              )}
          
          {/* Teilen Button - MOTIVIEREND */}
          <div className="share-section fade-in" style={{ animationDelay: '0.25s' }} data-testid="share-section">
            <button
              className={`share-btn ${isRamadanActive ? 'ramadan-share' : ''}`}
              onClick={handleShare}
              data-testid="share-button"
            >
              <span className="share-icon">
                <Heart size={14} />
                <Share2 size={16} />
              </span>
              <span>{t.share}</span>
            </button>
          </div>
          
          {/* Footer - HADITH */}
          <footer className="app-footer" data-testid="footer">
            <p className="footer-quote">
              {isRamadanActive ? (t.ramadanFooter || t.footerRamadan) : t.footerNormal}
            </p>
            {!isRamadanActive && (
              <p className="footer-source">{t.footerSource}</p>
            )}
            <div className="footer-links">
              <a href={t.privacyUrl} target="_blank" rel="noopener noreferrer" data-testid="privacy-link">
                {t.privacyPolicy}
              </a>
              <span className="footer-separator">•</span>
              <a href={t.supportUrl} target="_blank" rel="noopener noreferrer" data-testid="support-link">
                {t.support}
              </a>
              <span className="footer-separator">•</span>
              <button 
                className="footer-link-btn"
                onClick={() => setShowFeedbackModal(true)}
                data-testid="feedback-footer-btn"
              >
                {t.feedback || "Feedback"}
              </button>
            </div>
          </footer>
        </main>
      </div>
    </TooltipProvider>
  );
}

export default App;
